import React, { useCallback, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../../components/atoms';
import { DataTable } from '../../components/organisms';
import { DeleteConfirmationModal } from '../../components/organisms/DeleteConfirmationModal';
import { useArtists, useArtistActions } from '../../hooks/useArtists';
import type { Artist, CreateArtistRequest, UpdateArtistRequest } from '../../types/api/artist';
import type { Column, TableAction } from '../../types/table';
import { ArtistFormModal } from './components/ArtistFormModal';

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

export const ArtistManagementPage: React.FC = () => {
  const { fetchArtists } = useArtists();
  const { createArtist, updateArtist, getArtist, deleteArtist } = useArtistActions();

  const refreshKeyRef = useRef(0);
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleRefresh = useCallback(() => {
    refreshKeyRef.current += 1;
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedArtist(null);
    setIsCreateModalOpen(true);
  }, []);

  const handleEdit = useCallback(
    async (artist: Artist) => {
      try {
        const full = await getArtist(artist.artistId);
        setSelectedArtist(full);
        setIsEditModalOpen(true);
      } catch (error: unknown) {
        // Fall back to the row data if the detail fetch fails.
        setSelectedArtist(artist);
        setIsEditModalOpen(true);
        toast.error(getErrorMessage(error, 'Failed to load full artist details'));
      }
    },
    [getArtist]
  );

  const handleDelete = useCallback((artist: Artist) => {
    setSelectedArtist(artist);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCreateSubmit = useCallback(
    async (data: CreateArtistRequest | UpdateArtistRequest, imageFile?: File | null) => {
      try {
        await createArtist(data as CreateArtistRequest, imageFile || undefined);
        toast.success('Artist created successfully');
        setIsCreateModalOpen(false);
        handleRefresh();
      } catch (error: unknown) {
        toast.error(getErrorMessage(error, 'Failed to create artist'));
      }
    },
    [createArtist, handleRefresh]
  );

  const handleEditSubmit = useCallback(
    async (data: CreateArtistRequest | UpdateArtistRequest, imageFile?: File | null) => {
      if (!selectedArtist) return;
      try {
        await updateArtist(selectedArtist.artistId, data as UpdateArtistRequest, imageFile || undefined);
        toast.success('Artist updated successfully');
        setIsEditModalOpen(false);
        setSelectedArtist(null);
        handleRefresh();
      } catch (error: unknown) {
        toast.error(getErrorMessage(error, 'Failed to update artist'));
      }
    },
    [selectedArtist, updateArtist, handleRefresh]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedArtist) return;
    try {
      await deleteArtist(selectedArtist.artistId);
      toast.success('Artist deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedArtist(null);
      handleRefresh();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Failed to delete artist'));
    }
  }, [selectedArtist, deleteArtist, handleRefresh]);

  const columns = useMemo<Column<Artist>[]>(
    () => [
      { id: 'name', header: 'Name', accessor: 'name', minWidth: '200px' },
      { id: 'tracks', header: 'Tracks', accessor: 'trackCount', cell: (r) => r.trackCount ?? 0 },
      {
        id: 'status',
        header: 'Status',
        accessor: 'status',
        cell: (r) => r.status || '—',
      },
    ],
    []
  );

  const actions = useMemo<TableAction<Artist>[]>(
    () => [
      {
        id: 'edit',
        label: 'Edit',
        variant: 'ghost',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        ),
        onClick: handleEdit,
      },
      {
        id: 'delete',
        label: 'Delete',
        variant: 'danger',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        ),
        onClick: handleDelete,
      },
    ],
    [handleEdit, handleDelete]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-left">
          <h1 className="text-lg font-bold text-gray-900">Artists</h1>
          <p className="text-gray-600 mt-1 pl-3">Manage artist profiles for SAUNG</p>
        </div>
        <Button
          onClick={handleCreate}
          leftIcon={
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
          }
        >
          Add Artist
        </Button>
      </div>

      <DataTable<Artist>
        key={refreshKeyRef.current}
        columns={columns}
        fetchData={fetchArtists}
        searchable
        searchPlaceholder="Search artists..."
        initialPageSize={10}
        pageSizeOptions={[10, 20, 50, 100]}
        actions={actions}
        hoverable
        showRefreshButton
        onRefresh={handleRefresh}
        caption="Artist management table"
      />

      <ArtistFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateSubmit}
        mode="create"
      />

      <ArtistFormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedArtist(null);
        }}
        onSubmit={handleEditSubmit}
        mode="edit"
        artist={selectedArtist || undefined}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedArtist(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Artist"
        message={
          selectedArtist
            ? `Are you sure you want to delete "${selectedArtist.name}"? This action cannot be undone.`
            : 'Are you sure you want to delete this artist?'
        }
      />
    </div>
  );
};

export default ArtistManagementPage;
